import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCard, type CardResponse } from '@/api/crmCard';
import type { InboxConversation } from '@/api/inboxTypes';
import { useProjectStore } from '@/store/project';

/** Ключи кэша CRM-части переписки. Проект — в ключе: у каждого проекта своя область видимости CRM. */
export const crmKeys = {
  card: (project: string, acc: number, conv: number) => ['crm', 'card', project, acc, conv] as const,
  tasks: (project: string, dealId: string) => ['crm', 'tasks', project, dealId] as const,
  feed: (project: string, dealId: string) => ['crm', 'feed', project, dealId] as const,
  colleagues: (project: string) => ['crm', 'colleagues', project] as const,
  quick: (project: string, pipelineId: string) => ['crm', 'quick', project, pipelineId] as const,
  reasons: (project: string) => ['crm', 'reasons', project] as const,
  telephony: (project: string) => ['crm', 'telephony', project] as const,
};

/**
 * Карточка сделки по переписке (GET /api/crm/chatwoot/card). Ищет CRM: по переписке, контакту, почте, телефону.
 * Стадия, смененная в CRM кем-то другим, доезжает событием переписки (CRM отражает её в Chatwoot) —
 * на смену стадии у переписки карточку перечитываем.
 */
export function useDealCard(conv: InboxConversation | undefined, accountId: number, enabled: boolean) {
  const project = useProjectStore((s) => s.activeProjectId);
  const qc = useQueryClient();
  const key = crmKeys.card(project, accountId, conv?.id ?? 0);
  const q = useQuery<CardResponse>({
    queryKey: key,
    enabled: enabled && !!conv && !!project,
    queryFn: () => getCard({
      accountId,
      displayId: conv!.id,
      contactId: conv!.contact.id || undefined,
      email: conv!.contact.email || undefined,
      phone: conv!.contact.phone || undefined,
    }),
    staleTime: 30_000,
    retry: 1,
  });

  const stageAttr = conv?.crmStageId ?? null;
  useEffect(() => {
    // Не на первой отрисовке: тогда карточка и так грузится.
    if (!q.isFetched) return;
    void qc.invalidateQueries({ queryKey: key });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageAttr]);

  return { ...q, key, project };
}
